import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { homeSaga } from './saga';
import { HomeState } from './types';
import moment from 'moment';

export const initialState: HomeState = {
  tooltipDetails: {
    title: '',
    riskLevel: '',
    releaseDate: '',
    releaseDateKey: '',
    effectiveDate: '',
    effectiveDateKey: '',
    reportingSummary: undefined,
    lastUpdated: ''
  },
  releaseData: [],
  error: null,
  loading: false,
  silentRefreshing: false, // Background refresh without blocking UI
  mustWinViewTransitioning: false, // Must Wins view switch in progress (deferred render)
  timelineViewTransitioning: false, // Monthly/quarterly view switch in progress (deferred render)
  legend: false,
  isFullScreenMode: false,
  parkingLot: true,
  isCompact: false,
  mustWinViewActive: false, // Toggle for Must Wins view
  switchedToRoadmapForSearch: false, // Restore Must Wins when search is cleared
  collapseAll: true,
  unNestAll: false,
  previousUnNestAllState: null,
  unnestParentIds: [],
  highlightedIssue: {},
  highlightSource: null, // ✅ Track source of highlights
  searchFilterActive: false,
  searchFilteredItems: [],
  productGroups: {
    loading: false,
    error: null,
    data: [],
    selectedGroups: []
  },
  audiences: {
    loading: false,
    error: null,
    data: [],
    selectedAudiences: []
  },
  products: {
    loading: false,
    error: null,
    data: []
  },
  categories: {
    loading: false,
    error: null,
    selectedCats: [],
    data: []
  },
  events: {
    loading: false,
    error: null,
    selectedEvents: [],
    data: []
  },
  features: {
    loading: false,
    error: null,
    selectedFeatures: [],
    data: []
  },
  lanesTiers: {
    loading: false,
    error: null,
    selectedLanesTiers: [],
    data: []
  },
  status: {
    selectedStatus: []
  },
  // CSME-113550: independent preference — COMPLETED/CLOSED items shown by default.
  hideCompletedItems: false,
  productFilter: {
    loading: false,
    error: null,
    selectedProducts: [],
    data: []
  },
  selectedDates: {
    start: moment().format('YYYY-MM-DD'), // Custom start date if needed
    end: moment().add(6, 'months').format('YYYY-MM-DD')
  },
  filteredReleases: [],
  keyDatesRelease: null,
  hierarchyFilters: {
    loading: false,
    error: null,
    cached: false,
    customerAudiences: [],
    productGroups: [],
    categories: [],
    products: [],
    surfaces: [],
    categoryToProductMap: {},
    audienceStrategyMappings: {},
    validOptions: {
      customerAudiences: [],
      productGroups: [],
      categories: [],
      products: [],
      surfaces: [],
      categoryToProductMap: {},
      audienceStrategyMappings: {}
    },
    // Circuit breaker state to prevent infinite retries
    failureCount: 0,
    lastFailureTime: null,
    circuitBreakerOpen: false
  },
  categoryProductFilter: {
    selectedCategories: [],
    selectedProducts: []
  },
  genAiFilter: {
    selectedFilters: []
  },
  revenueImpactFilter: {
    selectedFilters: []
  },
  momentTypeFilter: {
    selectedMomentTypes: []
  },
  mustWinFilterOptions: {
    loading: false,
    error: null,
    data: []
  },
  hideEmptySwimLanes: false, // Hide empty swimlanes when filtering from Must Win dropdown
  mustWinDropdownSelection: null, // Track which item was selected from Must Win dropdown (for blue highlight)
  updateSuccessAlert: null,
  savedViewApplyToken: 0,
  activeSavedViewId: null as string | null,
  isMyStuffViewActive: false
};

const slice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    loadReleaseData(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    // Silent refresh - fetches data in background without blocking UI
    loadReleaseDataSilent(state, action: PayloadAction<any>) {
      state.silentRefreshing = true;
      state.error = null;
    },
    // Set refreshing flag only (no saga / no moments API) - e.g. milestones grid refresh
    setSilentRefreshing(state, action: PayloadAction<boolean>) {
      state.silentRefreshing = action.payload;
    },
    releaseDataloaded(state, action: PayloadAction<any>) {
      state.releaseData = action.payload;
      state.filteredReleases = action.payload?.releaseScheduled || [];
      state.loading = false;
      state.silentRefreshing = false; // Clear both loading states
    },
    loadReleaseDataError(state, action: PayloadAction<any>) {
      state.error = action.payload;
      state.loading = false;
      state.silentRefreshing = false; // Clear both loading states
    },
    loadProductGroups(state, action: PayloadAction<any>) {
      state.productGroups.loading = true;
      state.productGroups.error = null;
    },
    productGroupsLoaded(state, action: PayloadAction<any>) {
      state.productGroups.data = action.payload;
      state.productGroups.loading = false;
    },
    loadProductGroupsError(state, action: PayloadAction<any>) {
      state.productGroups.error = action.payload;
      state.productGroups.loading = false;
    },
    loadProducts(state, action: PayloadAction<any>) {
      state.productGroups.loading = true;
      state.productGroups.error = null;
    },
    productsLoaded(state, action: PayloadAction<any>) {
      state.products.data = action.payload;
      state.products.loading = false;
    },
    loadProductsError(state, action: PayloadAction<any>) {
      state.products.error = action.payload;
      state.products.loading = false;
    },
    loadCategories(state, action: PayloadAction<any>) {
      state.categories.loading = true;
      state.categories.error = null;
    },
    catgoriesLoaded(state, action: PayloadAction<any>) {
      state.categories.data = action.payload;
      state.categories.loading = false;
    },
    loadCategoriesError(state, action: PayloadAction<any>) {
      state.categories.error = action.payload;
      state.categories.loading = false;
    },
    loadEvents(state, action: PayloadAction<any>) {
      state.events.loading = true;
      state.events.error = null;
    },
    eventsLoaded(state, action: PayloadAction<any>) {
      state.events.data = action.payload;
      state.events.loading = false;
    },
    loadEventsError(state, action: PayloadAction<any>) {
      state.events.error = action.payload;
      state.events.loading = false;
    },

    setSelectedCats(state, action: PayloadAction<any>) {
      state.categories.selectedCats = action.payload;
      // state.loading = false;
    },
    setSelectedGroups(state, action: PayloadAction<any>) {
      state.productGroups.selectedGroups = action.payload;
      // state.loading = false;
    },
    setLegend(state, action: PayloadAction<any>) {
      state.legend = action.payload;
      // state.loading = false;
    },
    setCompact(state, action: PayloadAction<any>) {
      state.isCompact = action.payload;
      if (action.payload) {
        state.collapseAll = true;
        state.unNestAll = false;
      }
      // state.loading = false;
    },
    setIsFullScreenMode(state, action: PayloadAction<any>) {
      state.isFullScreenMode = action.payload;
    },
    setParkingLot(state, action: PayloadAction<any>) {
      state.parkingLot = action.payload;
      // state.loading = false;
    },
    setMustWinView(state, action: PayloadAction<boolean>) {
      state.mustWinViewActive = action.payload;
      if (action.payload) {
        state.isMyStuffViewActive = false;
      }
    },
    // Built-in "My Stuff" view toggle (GET /moments/my-stuff), mutually exclusive with Must Wins.
    setMyStuffView(state, action: PayloadAction<boolean>) {
      state.isMyStuffViewActive = action.payload;
      if (action.payload) {
        state.mustWinViewActive = false;
        state.mustWinDropdownSelection = null;
      }
    },
    setMustWinViewTransitioning(state, action: PayloadAction<boolean>) {
      state.mustWinViewTransitioning = action.payload;
    },
    setTimelineViewTransitioning(state, action: PayloadAction<boolean>) {
      state.timelineViewTransitioning = action.payload;
    },
    /** Switch to Roadmap view for search; clearSearchFilter will restore Must Wins when search is cleared */
    switchToRoadmapForSearch(state) {
      state.mustWinViewActive = false;
      state.switchedToRoadmapForSearch = true;
    },
    setCollapseAll(state) {
      state.collapseAll = !state.collapseAll;
      // Reset unNestAll state when collapsing all cards
    },
    setUnNestAll(state, action: PayloadAction<boolean>) {
      state.unNestAll = action.payload;
    },
    setDateRange(state, action: PayloadAction<any>) {
      state.selectedDates = action.payload;
      // state.loading = false;
    },
    updateHighlightedIssue: (
      state,
      action: PayloadAction<{
        relatedIssues: string[];
        unnestParentIds?: string[];
        source?: 'filter' | 'single-click' | 'search'; // ✅ Optional source parameter (added 'search')
      }>
    ) => {
      const { relatedIssues, unnestParentIds, source } = action.payload;

      // ✅ FIX: Merge with existing highlights instead of replacing
      // This preserves previously selected items (e.g., "In the Works" items)
      state.highlightedIssue = {
        ...state.highlightedIssue, // Keep existing highlights
        ...relatedIssues.reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {})
      };
      // Store the specific parent IDs to unnest
      state.unnestParentIds = unnestParentIds || [];
      // ✅ Track the source of highlights
      if (source) {
        state.highlightSource = source;
      }
    },
    clearHighlightedIssues: state => {
      // ✅ NEW: Action to completely clear all highlights
      // Used when clearing event filters or other filters
      state.highlightedIssue = {};
      state.unnestParentIds = [];
      state.highlightSource = null; // ✅ Reset source
    },
    setSelectedEvents(state, action: PayloadAction<any>) {
      state.events.selectedEvents = action.payload;
    },
    setSelectedEventsLabel(state, action: PayloadAction<any>) {
      state.events.selectedEventsLabel = action.payload;
    },
    loadFeatures(state, action: PayloadAction<any>) {
      state.features.loading = true;
      state.features.error = null;
    },
    featuresLoaded(state, action: PayloadAction<any>) {
      state.features.data = action.payload;
      state.features.loading = false;
    },
    loadFeaturesError(state, action: PayloadAction<any>) {
      state.features.error = action.payload;
      state.features.loading = false;
    },
    setSelectedFeatures(state, action: PayloadAction<any>) {
      state.features.selectedFeatures = action.payload;
    },
    loadAudiences(state, action: PayloadAction<any>) {
      state.audiences.loading = true;
      state.audiences.error = null;
    },
    audiencesLoaded(state, action: PayloadAction<any>) {
      state.audiences.data = action.payload;
      state.audiences.loading = false;
    },
    loadAudiencesError(state, action: PayloadAction<any>) {
      state.audiences.error = action.payload;
      state.audiences.loading = false;
    },
    setSelectedAudiences(state, action: PayloadAction<any>) {
      state.audiences.selectedAudiences = action.payload;
    },
    // loadProductFilter(state, action: PayloadAction<any>) {
    //   state.productFilter.loading = true;
    //   state.productFilter.error = null;
    // },
    productFilterLoaded(state, action: PayloadAction<any>) {
      state.productFilter.data = action.payload;
      state.productFilter.loading = false;
    },
    loadProductFilterError(state, action: PayloadAction<any>) {
      state.productFilter.error = action.payload;
      state.productFilter.loading = false;
    },
    setSelectedProducts(state, action: PayloadAction<any>) {
      state.productFilter.selectedProducts = action.payload;
    },
    setSelectedLanesTiers(state, action: PayloadAction<any>) {
      state.lanesTiers.selectedLanesTiers = action.payload;
    },
    setSelectedStatus(state, action: PayloadAction<any>) {
      state.status.selectedStatus = action.payload;
    },
    // CSME-113550: toggle for hiding COMPLETED/CLOSED items; independent of selectedStatus.
    setHideCompletedItems(state, action: PayloadAction<boolean>) {
      state.hideCompletedItems = action.payload;
    },
    setFilteredReleases(state, action: PayloadAction<any>) {
      state.filteredReleases = action.payload;
    },
    setKeyDatesRelease(state, action: PayloadAction<any | null>) {
      state.keyDatesRelease = action.payload;
    },
    setSearchFilterActive(state, action: PayloadAction<boolean>) {
      state.searchFilterActive = action.payload;
    },
    setSearchFilteredItems(state, action: PayloadAction<string[]>) {
      state.searchFilteredItems = action.payload;
    },
    clearSearchFilter(state) {
      state.searchFilterActive = false;
      state.searchFilteredItems = [];
      // Restore Must Wins view if we had switched to Roadmap only for search
      if (state.switchedToRoadmapForSearch) {
        state.mustWinViewActive = true;
        state.switchedToRoadmapForSearch = false;
      }
      // ✅ FIX: DON'T clear highlights here - event/filter highlights should persist
      // Search highlights are managed separately via updateHighlightedIssue/clearHighlightedIssues
      // This allows event highlights to persist when search is cleared or when slideout closes
      // state.highlightedIssue = {};  // REMOVED - was clearing event highlights too
      // Restore the previous unNestAll state if it was saved
      if (state.previousUnNestAllState !== null) {
        state.unNestAll = state.previousUnNestAllState;
        state.previousUnNestAllState = null;
      }
    },
    savePreviousUnNestAllState(state) {
      // Only save if we haven't already saved a previous state
      if (state.previousUnNestAllState === null) {
        state.previousUnNestAllState = state.unNestAll;
      }
    },
    clearPreviousUnNestAllState(state) {
      state.previousUnNestAllState = null;
    },
    // New unified hierarchy filters actions
    loadHierarchyFilters(state, action: PayloadAction<any>) {
      state.hierarchyFilters.loading = true;
      state.hierarchyFilters.error = null;
    },
    hierarchyFiltersLoaded(state, action: PayloadAction<any>) {
      state.hierarchyFilters.loading = false;
      state.hierarchyFilters.error = null;
      state.hierarchyFilters.cached = true;

      // Reset circuit breaker on successful load
      state.hierarchyFilters.failureCount = 0;
      state.hierarchyFilters.lastFailureTime = null;
      state.hierarchyFilters.circuitBreakerOpen = false;

      state.hierarchyFilters.customerAudiences =
        action.payload.customerAudiences || [];
      state.hierarchyFilters.productGroups = action.payload.productGroups || [];
      state.hierarchyFilters.categories = action.payload.categories || [];
      state.hierarchyFilters.products = action.payload.products || [];
      state.hierarchyFilters.surfaces = action.payload.surfaces || [];
      state.hierarchyFilters.categoryToProductMap =
        action.payload.categoryToProductMap || {};
      state.hierarchyFilters.audienceStrategyMappings =
        action.payload.audienceStrategyMappings || {};
      // Initially, all options are valid
      state.hierarchyFilters.validOptions = {
        customerAudiences: action.payload.customerAudiences || [],
        productGroups: action.payload.productGroups || [],
        categories: action.payload.categories || [],
        products: action.payload.products || [],
        surfaces: action.payload.surfaces || [],
        categoryToProductMap: action.payload.categoryToProductMap || {},
        audienceStrategyMappings: action.payload.audienceStrategyMappings || {}
      };
    },
    loadHierarchyFiltersError(state, action: PayloadAction<any>) {
      state.hierarchyFilters.loading = false;
      state.hierarchyFilters.error = action.payload;

      // Circuit breaker: Track failures and open circuit after threshold
      const MAX_FAILURES = 3;
      const CIRCUIT_RESET_TIME = 5 * 60 * 1000; // 5 minutes

      state.hierarchyFilters.failureCount += 1;
      state.hierarchyFilters.lastFailureTime = Date.now();

      // If it's a 404 error, mark as cached to prevent continuous retries
      const error = action.payload;
      const status = error?.response?.status || error?.status;

      if (status === 404) {
        state.hierarchyFilters.cached = true;
        state.hierarchyFilters.circuitBreakerOpen = true;
        console.warn(
          '🚫 Hierarchy API returned 404 - marking as cached to prevent retries'
        );
      } else if (state.hierarchyFilters.failureCount >= MAX_FAILURES) {
        // Open circuit breaker after max failures
        state.hierarchyFilters.circuitBreakerOpen = true;
        console.error(
          `🚫 Circuit breaker OPEN for product hierarchy filters after ${MAX_FAILURES} failures. Will retry after ${
            CIRCUIT_RESET_TIME / 60000
          } minutes.`
        );
      } else {
        console.warn(
          `⚠️ Product hierarchy filter failed (${state.hierarchyFilters.failureCount}/${MAX_FAILURES} failures)`
        );
      }
    },
    updateValidOptions(state, action: PayloadAction<any>) {
      state.hierarchyFilters.loading = false;
      state.hierarchyFilters.error = null;
      // Don't set cached = true here, as this is just updating valid options, not reloading all data
      state.hierarchyFilters.validOptions = {
        customerAudiences: action.payload.customerAudiences || [],
        productGroups: action.payload.productGroups || [],
        categories: action.payload.categories || [],
        products: action.payload.products || [],
        surfaces: action.payload.surfaces || [],
        categoryToProductMap: action.payload.categoryToProductMap || {},
        audienceStrategyMappings: action.payload.audienceStrategyMappings || {}
      };

      // DO NOT update main data arrays - they should contain ALL available options
      // Only validOptions should be updated to reflect cross-filtering constraints
    },
    // Actions for Category-Product hierarchical filter
    setCategoryProductSelection(
      state,
      action: PayloadAction<{ categories: string[]; products: string[] }>
    ) {
      state.categoryProductFilter.selectedCategories =
        action.payload.categories;
      state.categoryProductFilter.selectedProducts = action.payload.products;
    },
    clearCategoryProductSelection(state) {
      state.categoryProductFilter.selectedCategories = [];
      state.categoryProductFilter.selectedProducts = [];
    },
    // New action for updating moment title
    updateMomentDetails(
      state,
      action: PayloadAction<{
        issueId: string;
        momentType: string;
        releasePhaseId?: string;
        refreshSource?: 'reviewApproval';
        fields: Partial<{
          title: string;
          riskLevel: string;
          releaseDate: string;
          releaseDateKey: string;
          effectiveDate: string;
          effectiveDateKey: string;
          reportingSummary: string;
          lastUpdated: string;
        }>;
      }>
    ) {
      // This action will be handled by saga, no state changes needed here
    },
    // Action for updating tooltip details
    updateTooltipDetails(
      state,
      action: PayloadAction<
        Partial<{
          title: string;
          riskLevel: string;
          releaseDate: string;
          releaseDateKey: string;
          effectiveDate: string;
          effectiveDateKey: string;
          reportingSummary: string;
          lastUpdated: string;
        }>
      >
    ) {
      // Merge only the provided fields with existing state
      state.tooltipDetails = { ...state.tooltipDetails, ...action.payload };
    },
    // Action for resetting tooltip details to initial state
    resetTooltipDetails(state) {
      state.tooltipDetails = {
        title: '',
        riskLevel: '',
        releaseDate: '',
        releaseDateKey: '',
        effectiveDate: '',
        effectiveDateKey: '',
        reportingSummary: undefined,
        lastUpdated: ''
      };
    },
    // Action for setting update success alert
    setUpdateSuccessAlert(
      state,
      action: PayloadAction<{
        message: string;
        issueId: string;
        releasePhaseId: string;
      }>
    ) {
      state.updateSuccessAlert = action.payload;
    },
    // Action for clearing update success alert
    clearUpdateSuccessAlert(state) {
      state.updateSuccessAlert = null;
    },
    // Clear all filters then apply saved-view params in one atomic update.
    replaceViewStateFromSavedView(
      state,
      action: PayloadAction<{
        startDate?: string;
        endDate?: string;
        products?: string[];
        productGroups?: string[];
        categories?: string[];
        audiences?: string[];
        events?: string[];
        features?: string[];
        status?: string[];
        tiers?: string[];
        genAiFilters?: string[];
        revenueImpactFilters?: string[];
        momentTypeFilters?: string[];
        categoryProductCategories?: string[];
        categoryProductProducts?: string[];
        surfaces?: string[];
        isCompact?: boolean;
        showParkingLot?: boolean;
        collapseAll?: boolean;
        unNestAll?: boolean;
        mustWin?: boolean;
      }>
    ) {
      const params = action.payload;

      // Opening a real saved view always exits the built-in My Stuff view.
      state.isMyStuffViewActive = false;

      state.productGroups.selectedGroups = [];
      state.audiences.selectedAudiences = [];
      state.categories.selectedCats = [];
      state.events.selectedEvents = [];
      state.features.selectedFeatures = [];
      state.productFilter.selectedProducts = [];
      state.status.selectedStatus = [];
      state.lanesTiers.selectedLanesTiers = [];
      state.genAiFilter.selectedFilters = [];
      state.revenueImpactFilter.selectedFilters = [];
      state.momentTypeFilter.selectedMomentTypes = [];
      state.categoryProductFilter.selectedCategories = [];
      state.categoryProductFilter.selectedProducts = [];
      state.searchFilterActive = false;
      state.searchFilteredItems = [];
      state.highlightedIssue = {};
      state.highlightSource = null;
      state.mustWinDropdownSelection = null;
      state.hideEmptySwimLanes = false;
      state.unnestParentIds = [];

      const categories = params.categoryProductCategories?.length
        ? params.categoryProductCategories
        : params.categories ?? [];
      const products = params.categoryProductProducts?.length
        ? params.categoryProductProducts
        : params.products ?? [];
      const surfaceFilters = params.features ?? [];

      state.productGroups.selectedGroups = params.productGroups ?? [];
      state.audiences.selectedAudiences = params.audiences ?? [];
      state.categories.selectedCats = categories;
      state.events.selectedEvents = params.events ?? [];
      state.features.selectedFeatures = surfaceFilters;
      state.productFilter.selectedProducts = products;
      state.status.selectedStatus = params.status ?? [];
      state.lanesTiers.selectedLanesTiers = params.tiers ?? [];
      state.genAiFilter.selectedFilters = params.genAiFilters ?? [];
      state.revenueImpactFilter.selectedFilters =
        params.revenueImpactFilters ?? [];
      state.momentTypeFilter.selectedMomentTypes =
        params.momentTypeFilters ?? [];
      state.categoryProductFilter.selectedCategories = categories;
      state.categoryProductFilter.selectedProducts = products;

      if (params.startDate && params.endDate) {
        state.selectedDates = {
          start: params.startDate,
          end: params.endDate
        };
      }

      if (params.isCompact !== undefined) {
        state.isCompact = params.isCompact;
      }
      if (params.showParkingLot !== undefined) {
        state.parkingLot = params.showParkingLot;
      }
      if (params.collapseAll !== undefined) {
        state.collapseAll = params.collapseAll;
      }
      if (params.unNestAll !== undefined) {
        state.unNestAll = params.unNestAll;
      }
      // Opening a saved view always exits Must Wins by default - mirrors the
      // isMyStuffViewActive reset above. Milestones-created saved views never
      // capture a mustWin flag at all (Must Wins is Roadmap-only), so without
      // this defaulting to false, opening one while Must Wins was active left
      // it stuck on. Only a Roadmap saved view that captured mustWin: true
      // re-enables it.
      state.mustWinViewActive = params.mustWin ?? false;
      if (!params.mustWin) {
        state.mustWinDropdownSelection = null;
      }
    },
    bumpSavedViewApplyToken(state) {
      state.savedViewApplyToken += 1;
    },
    setActiveSavedViewId(state, action: PayloadAction<string | null>) {
      state.activeSavedViewId = action.payload;
    },
    // Apply view state from deep link URL
    applyViewStateFromUrl(
      state,
      action: PayloadAction<{
        startDate?: string;
        endDate?: string;
        products?: string[];
        productGroups?: string[];
        categories?: string[];
        audiences?: string[];
        events?: string[];
        features?: string[];
        status?: string[];
        tiers?: string[];
        genAiFilters?: string[];
        revenueImpactFilters?: string[];
        momentTypeFilters?: string[];
        categoryProductCategories?: string[];
        categoryProductProducts?: string[];
        surfaces?: string[];
        isCompact?: boolean;
        showParkingLot?: boolean;
        collapseAll?: boolean;
        unNestAll?: boolean;
        searchQuery?: string;
        mustWin?: boolean;
        issueIds?: string[];
      }>
    ) {
      const params = action.payload;

      // Deep links default to the normal view; the myStuff-specific branch
      // in Home/index.tsx re-enables it afterward when the link encodes it.
      state.isMyStuffViewActive = false;

      // Apply date range
      if (params.startDate && params.endDate) {
        state.selectedDates = {
          start: params.startDate,
          end: params.endDate
        };
      }

      // Apply filters
      if (params.productGroups && params.productGroups.length > 0) {
        state.productGroups.selectedGroups = params.productGroups;
      }
      if (params.categories && params.categories.length > 0) {
        state.categories.selectedCats = params.categories;
      }
      if (params.audiences && params.audiences.length > 0) {
        state.audiences.selectedAudiences = params.audiences;
      }
      if (params.events && params.events.length > 0) {
        state.events.selectedEvents = params.events;
      }
      if (params.features && params.features.length > 0) {
        state.features.selectedFeatures = params.features;
      }
      if (params.status && params.status.length > 0) {
        state.status.selectedStatus = params.status;
      }
      if (params.tiers && params.tiers.length > 0) {
        state.lanesTiers.selectedLanesTiers = params.tiers;
      }
      if (params.products && params.products.length > 0) {
        state.productFilter.selectedProducts = params.products;
      }
      if (params.genAiFilters && params.genAiFilters.length > 0) {
        state.genAiFilter.selectedFilters = params.genAiFilters;
      }
      if (
        params.revenueImpactFilters &&
        params.revenueImpactFilters.length > 0
      ) {
        state.revenueImpactFilter.selectedFilters = params.revenueImpactFilters;
      }
      if (params.momentTypeFilters && params.momentTypeFilters.length > 0) {
        state.momentTypeFilter.selectedMomentTypes = params.momentTypeFilters;
      }
      if (
        params.categoryProductCategories &&
        params.categoryProductCategories.length > 0
      ) {
        state.categoryProductFilter.selectedCategories =
          params.categoryProductCategories;
      }
      if (
        params.categoryProductProducts &&
        params.categoryProductProducts.length > 0
      ) {
        state.categoryProductFilter.selectedProducts =
          params.categoryProductProducts;
      }

      // Apply view toggles
      if (params.isCompact !== undefined) {
        state.isCompact = params.isCompact;
      }
      if (params.showParkingLot !== undefined) {
        state.parkingLot = params.showParkingLot;
      }
      if (params.collapseAll !== undefined) {
        state.collapseAll = params.collapseAll;
      }
      if (params.unNestAll !== undefined) {
        state.unNestAll = params.unNestAll;
      }

      // Must Wins view toggle
      if (params.mustWin !== undefined) {
        state.mustWinViewActive = params.mustWin;
        if (!params.mustWin) {
          state.mustWinDropdownSelection = null;
        }
      }

      // Apply search (if implementing search filter)
      if (params.searchQuery) {
        state.searchFilterActive = true;
        // Note: You'd need to implement search logic to populate searchFilteredItems
      }

      // NOTE: issueIds from URL are intentionally NOT applied here.
      // They must be applied after releaseData loads (handled in Home/index.tsx).
    },
    // Actions for Gen AI filter
    setGenAiFilters(state, action: PayloadAction<string[]>) {
      state.genAiFilter.selectedFilters = action.payload;
    },
    clearGenAiFilters(state) {
      state.genAiFilter.selectedFilters = [];
    },
    // Actions for Revenue Impact filter
    setRevenueImpactFilters(state, action: PayloadAction<string[]>) {
      state.revenueImpactFilter.selectedFilters = action.payload;
    },
    clearRevenueImpactFilters(state) {
      state.revenueImpactFilter.selectedFilters = [];
    },
    setSelectedMomentTypes(state, action: PayloadAction<string[]>) {
      state.momentTypeFilter.selectedMomentTypes = action.payload;
    },
    clearMomentTypeFilter(state) {
      state.momentTypeFilter.selectedMomentTypes = [];
    },
    // Action to control hiding empty swimlanes (for Must Win dropdown)
    setHideEmptySwimLanes(state, action: PayloadAction<boolean>) {
      state.hideEmptySwimLanes = action.payload;
    },
    // Action to track which item was selected from Must Win dropdown (for blue highlight)
    setMustWinDropdownSelection(state, action: PayloadAction<string | null>) {
      state.mustWinDropdownSelection = action.payload;
    },
    // Must Win filter options (for Left sidebar list only; no filter state)
    loadMustWinFilterOptions(state) {
      state.mustWinFilterOptions.loading = true;
      state.mustWinFilterOptions.error = null;
    },
    mustWinFilterOptionsLoaded(state, action: PayloadAction<any>) {
      state.mustWinFilterOptions.data = action.payload;
      state.mustWinFilterOptions.loading = false;
    },
    loadMustWinFilterOptionsError(state, action: PayloadAction<any>) {
      state.mustWinFilterOptions.error = action.payload;
      state.mustWinFilterOptions.loading = false;
    },
    // Manual circuit breaker reset for product hierarchy filters
    resetHierarchyFiltersCircuitBreaker(state) {
      state.hierarchyFilters.failureCount = 0;
      state.hierarchyFilters.lastFailureTime = null;
      state.hierarchyFilters.circuitBreakerOpen = false;
      state.hierarchyFilters.error = null;
      state.hierarchyFilters.cached = false;
    }
  }
});

export const { actions: homeActions } = slice;

export const useHomeSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: homeSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useHomeSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
